// @flow
import * as React from 'react';
import { ButtonBase, Stack, Typography } from '@mui/material';
import { useClient } from '../../hooks/client-context';
import { getAgeByBirthdate, getUserName, isPersonEligible } from '@coverright/utils';
import moment from 'moment';
import { formatToPhoneNumber } from '@coverright/ui/inputs';
import { Copy } from '@phosphor-icons/react';
import { withButton } from '../../../../features';
import { ContactFormEditButton } from './contact-info-form';
import { enqueueSnackbar } from 'notistack';
import { GenderView, Record, Tobacco } from '../../../../shared';
import { MedicareCard } from './medicare-card';
import { MedicareCardView } from '@coverright/features';

const Info = () => {
  const client = useClient();

  const isLocationSet = client.zip && client.countyName;
  const location = isLocationSet ? `${client.zip} ${client.countyName}${client.state ? ', ' + client.state : ''}` : 'You have to set location for quoting';
  const birthDate = client.birthDate ? `${moment(client.birthDate).format('MM/DD/YYYY')} (${getAgeByBirthdate(client.birthDate)} y.o.)` : ''

  return (
    <Stack gap={1}>
      <Typography variant={'subtitle1'}>Contact info</Typography>

      <Record title={'Created at'} value={moment(client.createdAt).format('MM/DD/YYYY HH:mm')} />
      <Record title={'Name'} value={<Stack gap={1} direction={'row'} alignItems={'center'}>
        <CopyButton onClick={() => {
          navigator.clipboard.writeText(getUserName(client)).then(() => enqueueSnackbar('Copied!', {variant: "info"}))
        }} />
        <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{getUserName(client)}</Typography>
      </Stack>} />
      <Record title={'Email'} value={client.email && <Stack gap={1} direction={'row'} alignItems={'center'}>
        <CopyButton onClick={() => {
          navigator.clipboard.writeText(client.email!).then(() => enqueueSnackbar('Copied!', {variant: "info"}))
        }} />
        <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{client.email}</Typography>
      </Stack>} />
      <Record title={'Location'} value={isLocationSet ? <Stack gap={1} direction={'row'} alignItems={'center'}>
          <CopyButton onClick={() => {
            navigator.clipboard.writeText(client.zip!).then(() => enqueueSnackbar('Copied!', {variant: "info"}))
          }} />
          <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{location}</Typography>
        </Stack> : location} tooltip={location}
              color={!isLocationSet ? 'error' : 'default'} />
      <Record title={'Phone'} value={client.phoneNumber ? <Stack gap={1} direction={'row'} alignItems={'center'}>
        <CopyButton onClick={() => {
          navigator.clipboard.writeText(client.phoneNumber!.replace('+1', '').replace(/\D/g, ''))
            .then(() => enqueueSnackbar('Copied!', {variant: "info"}))
        }} />
        <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{formatToPhoneNumber(client.phoneNumber)}</Typography>
      </Stack> : undefined} />
      {client.phoneNumber2 && <Record title={'Sec. phone'} tooltip={'Secondary phone'} value={<Stack gap={1} direction={'row'} alignItems={'center'}>
        <CopyButton onClick={() => {
          navigator.clipboard.writeText(client.phoneNumber2!.replace('+1', '').replace(/\D/g, ''))
            .then(() => enqueueSnackbar('Copied!', {variant: "info"}))
        }} />
        <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{formatToPhoneNumber(client.phoneNumber2)}</Typography>
      </Stack>} />}
      <Record title={'Birth date / Age'} value={birthDate ? <Stack gap={1} direction={'row'} alignItems={'center'}>
        <CopyButton onClick={() => {
          navigator.clipboard.writeText(moment(client.birthDate).format('MM/DD/YYYY'))
            .then(() => enqueueSnackbar('Copied!', {variant: "info"}))
        }} />
        <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>{birthDate}</Typography>
      </Stack> : undefined} />
      <Record title={'Tobacco'} value={<Tobacco value={client.tobacco} />} />
      <Record title={'Gender'} value={<GenderView value={client.gender} />} />
      <Record title={'Client Logged In'} value={(
        <Typography fontWeight={500} fontSize={14} color={`${client.userId ? 'success' : 'error'}.500`}>
          {client.userId ? 'Yes' : 'No'}
        </Typography>
      )} />
      {(client.adviserName || client.adviserEmail) && <Record title={'Financial Advisor'} value={(
        <Stack>
          {!!client.adviserName && <Typography fontWeight={500} fontSize={14} noWrap overflow={'hidden'} textOverflow={'ellipsis'}>
            {client.adviserName}
          </Typography>}
          {!!client.adviserEmail && <Typography variant={'caption'} color={'text.secondary'}>
            {client.adviserEmail}
          </Typography>}
          {!!client.adviserPhone && <Typography variant={'caption'} color={'text.secondary'}>
            {formatToPhoneNumber(client.adviserPhone)}
          </Typography>}
        </Stack>
      )} />}
      {/*<Record title={'Cid'} value={client.cId} />*/}

      <MedicareCard />
    </Stack>
  );
};

const CopyButton = ({ onClick }: {onClick: () => void}) => (
  <ButtonBase onClick={onClick}>
    <Copy size={16} weight={'bold'} color="#a6a6a6" />
  </ButtonBase>
)

export const ContactInfo = withButton(
  Info,
  ContactFormEditButton
)
